import { useState, useEffect, useRef, memo, createContext, useContext } from 'react';
import { motion, useInView, AnimatePresence } from "framer-motion";
import './assets/fonts/fonts.css';
import './App.css';
import { initCursor, disposeCursor, updateCursor } from 'ipad-cursor';
import { Moon, Sun } from 'lucide-react';

const ThemeContext = createContext('dark');

const DynamicIsland = memo(() => {
  const theme = useContext(ThemeContext);
  const [time, setTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const options = {
        timeZone: 'America/Toronto',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      };
      const estTime = new Date().toLocaleString('en-US', options);
      setTime(estTime);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={`inline-block text-sm flex font-inter font-normal max-w-sm ${theme === 'dark' ? 'text-white' : 'text-black'}`}
    >
      <div className="flex items-center w-full gap-1.5">
        <span className={`text-sm ${theme === 'dark' ? 'text-white/60' : 'text-black/50'}`}>Toronto</span>
        <motion.p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
          {time}
        </motion.p>
        <div className="flex-grow"></div>
        <div className="w-[14px] flex items-center justify-center">
        <div className="relative w-2 h-2">
          <motion.div
            className="absolute inset-0 rounded-full bg-green-500"
            animate={{
              scale: [0, 0.5, 2],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              times: [0, 0.15, 1],
              ease: "linear"
            }}
          />
          <div className="absolute inset-0 rounded-full bg-green-500" />
        </div>
        </div>
      </div>
    </div>
  );
});

const Profile = memo(() => {
  const theme = useContext(ThemeContext);
  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
        className="mb-4"
      >
        <h1 data-cursor="text" className={`text-[18px] md:text-[20px] font-inter leading-none ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Mohammed Ibrahim</h1>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
      >
          <h3 data-cursor="text" className={`font-inter mb-3 opacity-60 ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Software engineer and designer based in Toronto</h3>
        </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 0.25 }}
        className="flex lg:hidden justify-between mb-3"
      >
        <a href="https://x.com/mohams2001" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-white/50' : 'text-black/40'} font-inter`}>X</a>
        <a href="https://instagram.com/mohams3ios01" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-white/50' : 'text-black/40'} font-inter`}>Instagram</a>
        <a href="https://linkedin.com/in/mohammed2ibrahim" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-white/50' : 'text-black/40'} font-inter`}>LinkedIn</a>
        <a href="/files/Resume.pdf" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-white/50' : 'text-black/40'} font-inter`}>Resume</a>
      </motion.div>

      </div>
    );
  });

const About = memo(() => {
  const theme = useContext(ThemeContext);
  const textColor = theme === 'dark' ? 'text-white' : 'text-black';
  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 0.3 }}
        >
          <p data-cursor="text" className={`${textColor} font-inter font-[400] mb-3 text-left`} style={{ lineHeight: '1.25rem' }}>From the age of 13, I started building software. I opened a web design agency, cold-calling small businesses in my city and building websites.</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut", delay: 0.4 }}
        >
          <p data-cursor="text" className={`${textColor} font-inter font-[400] mb-3 text-left`} style={{ lineHeight: '1.25rem' }}>Since then, I've taken an extreme interest in building mobile apps for iOS. I enjoy designing everything from the user interface to the backend architecture of any project I take on.</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut", delay: 0.5 }}
        >
          <p data-cursor="text" className={`${textColor} font-inter font-[400] mb-3 text-left`} style={{ lineHeight: '1.25rem' }}>I build with craft in mind and strive to build the best user experiences.</p>
        </motion.div>
    </div>
  );
});

const PreviouslyWorked = memo(() => {
  const theme = useContext(ThemeContext);
  const textColor = theme === 'dark' ? 'text-white' : 'text-black';
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div className="w-full flex justify-center mt-6 text-[.875rem]" ref={ref}>
      <div className="max-w-xl w-full px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: "easeOut", delay: isInView ? 0.6 : 0 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <p data-cursor="text" className={`${textColor} font-inter text-left`} style={{ lineHeight: '1.25rem' }}>Projects</p>
            <div className={`flex-grow h-[1px] rounded-full ${theme === 'dark' ? 'bg-white/15' : 'bg-black/15'}`}></div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ duration: 0.4, ease: "easeOut", delay: isInView ? 0.7 : 0 }}
        >
          <WorkItem company="Rabbithole" title="rabbithole.chat" titleLink="https://rabbithole.chat" startDate="2025" endDate="" isFirst={true} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ duration: 0.4, ease: "easeOut", delay: isInView ? 0.8 : 0 }}
          className="mt-8"
        >
          <div className="flex items-center gap-4 mb-6">
            <p data-cursor="text" className={`${textColor} font-inter text-left`} style={{ lineHeight: '1.25rem' }}>Work</p>
            <div className={`flex-grow h-[1px] rounded-full ${theme === 'dark' ? 'bg-white/15' : 'bg-black/15'}`}></div>
          </div>
        </motion.div>

        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className=""
          >
            {[
              { company: "OpenAI", title: "Software Engineer", startDate: "2025", endDate: "Present", link: "https://openai.com", image: "/icons/openai.png" },
              { company: "222", title: "Software Engineer", startDate: "2026", endDate: "", link: "https://222.place", image: "/icons/222.png" },
              { company: "Software Applications Incorporated", title: "Software Engineer", startDate: "2025", endDate: "", link: "https://software.inc/", image: "/icons/sai.png" },
              { company: "Interaction Company of California", title: "Software Engineer", startDate: "2025", endDate: "", link: "https://poke.com", image: "/icons/interaction.png" },
              { company: "Sana", title: "Software Engineer", startDate: "2025", endDate: "", link: "https://sana.ai", image: "/icons/sana.png" },
            ].map((workItem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ duration: 0.4, ease: "easeOut", delay: isInView ? 0.9 + index * 0.05 : 0 }}
              >
                <WorkItem
                  company={workItem.company}
                  title={workItem.title}
                  startDate={workItem.startDate}
                  endDate={workItem.endDate}
                  link={workItem.link}
                  image={workItem.image}
                  isFirst={index === 0}
                />
              </motion.div>
            ))}
          </motion.div>

          {[
            { company: "Boom", title: "Founding Software Engineer", startDate: "2022", endDate: "2025", link: "https://www.boomvideo.app/", image: "/icons/boom.png" },
            { company: "Apple", title: "Software Engineer Intern", startDate: "2021, 2023", endDate: "", link: "https://apple.com", image: "/icons/apple.png" },
            { company: "Twitter", title: "Software Engineer Intern", startDate: "2022", endDate: "", link: "https://x.com", image: "/icons/twitter.png" },
            { company: "Shopify", title: "Software Engineer Intern", startDate: "2020", endDate: "2021", link: "https://shopify.com", image: "/icons/shopify.png" },
          ].map((workItem, index) => (
            <motion.div
              key={index + 5}
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ duration: 0.4, ease: "easeOut", delay: isInView ? 1.15 + index * 0.05 : 0 }}
            >
              <WorkItem
                company={workItem.company}
                title={workItem.title}
                startDate={workItem.startDate}
                endDate={workItem.endDate}
                link={workItem.link}
                image={workItem.image}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
});

const WorkItem = memo(({ company, title, startDate, endDate, isFirst, link, titleLink, image }) => {
  const theme = useContext(ThemeContext);
  const textColor = theme === 'dark' ? 'text-white' : 'text-black';
  const [hovered, setHovered] = useState(false);
  const needsInvert = (company === "Apple" || company === "OpenAI") && theme === 'dark';
  return (
    <div
      className={`w-full flex justify-center ${isFirst ? 'mt-0' : 'mt-4'}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="max-w-xl w-full">
        <div className="flex flex-row items-center">
          {image && (
            <AnimatePresence>
              {hovered && (
                <motion.img
                  src={image}
                  alt={`${company} logo`}
                  className="w-4 h-4 rounded-sm object-contain"
                  initial={{ opacity: 0, width: 0, marginRight: 0, filter: `blur(4px) ${needsInvert ? 'invert(1)' : ''}` }}
                  animate={{ opacity: 1, width: 16, marginRight: 6, filter: `blur(0px) ${needsInvert ? 'invert(1)' : ''}` }}
                  exit={{ opacity: 0, width: 0, marginRight: 0, filter: `blur(4px) ${needsInvert ? 'invert(1)' : ''}` }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                />
              )}
            </AnimatePresence>
          )}
          {link ? (
            <a href={link} target="_blank" rel="noopener noreferrer" data-cursor="text" className={`${textColor} font-inter text-left`} style={{ lineHeight: '1.25rem' }}>{company}</a>
          ) : (
            <p data-cursor="text" className={`${textColor} font-inter text-left`} style={{ lineHeight: '1.25rem' }}>{company}</p>
          )}
          {titleLink ? (
            <a href={titleLink} target="_blank" rel="noopener noreferrer" data-cursor="text" className={`${textColor} opacity-40 font-inter text-left ml-2`} style={{ lineHeight: '1.25rem' }}>{title}</a>
          ) : (
            <p data-cursor="text" className={`${textColor} font-inter text-left opacity-40 ml-2`} style={{ lineHeight: '1.25rem' }}>{title}</p>
          )}
          <div className="flex-grow"></div>
          <p data-cursor="text" className={`${textColor} font-inter text-right opacity-40`} style={{ lineHeight: '1.25rem' }}>{endDate ? `${startDate} - ${endDate}` : startDate}</p>
        </div>
      </div>
    </div>
  );
});

function NewPage() {
  const [theme, setTheme] = useState(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  });

  useEffect(() => {
    document.body.style.backgroundColor = theme === 'dark' ? '#171717' : '#f5f5f5';
  }, [theme]);

  useEffect(() => {
    initCursor();
    updateCursor();

    const handleResize = () => {
      if (window.innerWidth <= 768) {
        console.log("Cursor disposed");
        disposeCursor();
      } else {
        initCursor();
      }
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      disposeCursor();
    };
  }, []);

  return (
    <ThemeContext.Provider value={theme}>
    <div className={`min-h-screen flex flex-col pb-10 md:pb-20 transition-colors duration-300 ${theme === 'dark' ? 'bg-[#171717]' : 'bg-[#f5f5f5]'}`}>
      <div className="relative w-full">
        <motion.nav
          className="hidden lg:flex fixed top-20 bottom-10 font-inter text-[14px] flex-col w-[160px]"
          style={{ left: 'calc(50% - 288px - 200px)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 1.6 }}
        >
          <ul className="flex flex-col gap-3">
            <li><a href="#about" className={`${theme === 'dark' ? 'text-white' : 'text-black'}`}>About</a></li>
            <li><a href="https://x.com/mohams2001" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-white/50 hover:text-white' : 'text-black/40 hover:text-black'}`}>X</a></li>
            <li><a href="https://instagram.com/mohams3ios01" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-white/50 hover:text-white' : 'text-black/40 hover:text-black'}`}>Instagram</a></li>
            <li><a href="https://linkedin.com/in/mohammed2ibrahim" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-white/50 hover:text-white' : 'text-black/40 hover:text-black'}`}>LinkedIn</a></li>
            <li><a href="/files/Resume.pdf" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-white/50 hover:text-white' : 'text-black/40 hover:text-black'}`}>Resume</a></li>
          </ul>
          <img src="/signature-cropped.png" alt="Signature" className={`w-32 mt-10 ${theme === 'dark' ? 'invert' : ''}`} style={{ maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.75) 100%)', WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.75) 100%)' }} />
          <div className="flex-grow"></div>
          <DynamicIsland />
          <div className={`h-[1px] w-full rounded-full mb-3 mt-3 ${theme === 'dark' ? 'bg-white/15' : 'bg-black/15'}`}></div>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className={`${theme === 'dark' ? 'text-white/50 hover:text-white' : 'text-black/40 hover:text-black'} transition-colors flex items-center justify-between w-full`}
          >
            <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
            <AnimatePresence mode="wait">
              <motion.div
                key={theme}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: "spring", duration: 0.15, bounce: 0.5 }}
              >
                {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
              </motion.div>
            </AnimatePresence>
          </button>
        </motion.nav>

        <div className="max-w-xl w-full flex flex-col px-4 md:px-6 mb-6 md:mb-10 mt-10 md:mt-20 mx-auto text-[.875rem]">
          <div className="flex flex-col w-full gap-2">
            <div className="w-full text-left">
              <Profile />
            </div>
            <div className="w-full">
              <About />
            </div>
          </div>
        </div>

        <PreviouslyWorked />
      </div>
    </div>
    </ThemeContext.Provider>
  );
}

export default NewPage;
